import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { blogPostPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import { type Locale, getAllLocales } from '@lib/language'
import { useBlogCanonicalUrl } from '@lib/metadata'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
  getPageUrl,
} from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogLocale,
  getBlogPostPage,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { getBlogPostSchema } from '@lib/schema/blog'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogPostPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogPostPage
  site: SanitySiteFragment
}

const BlogPostPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogPostPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl('blogPost', slug, site.blogSettings)

  const blogPostSchema = getBlogPostSchema(
    locale,
    page,
    site.generalSettings.siteURL,
  )
  const organizationUrl = site.schemaSettings?.organization?.url
  const blogPageUrl = getPageUrl({
    locale,
    pageType: 'blogPage',
  })
  const blogPostUrl = getPageUrl({
    locale,
    pageType: 'blogPost',
    slugs: slug,
  })
  const breadcrumbListSchema = getBreadcrumbListSchema([
    {
      title: page.blogPage?.seo?.metaTitle,
      url: `${organizationUrl}${blogPageUrl}`,
    },
    {
      title: page.seo?.metaTitle ?? page.title,
      url: `${organizationUrl}${blogPostUrl}`,
    },
  ])
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [blogPostSchema, breadcrumbListSchema, ...videoObjectSchemas]

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={blogPostPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogPostSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogPostPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogPostPageParams>,
): Promise<GetStaticPropsResult<BlogPostPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const slug = context.params?.slug ?? ''
  const blogPostPage = await getBlogPostPage(
    sanityClient,
    context.locale,
    blogLocale,
    slug,
  )

  if (!blogPostPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: getNonNullValues(blogPostPage.page),
      site: getNonNullValues(blogPostPage.site),
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogPostPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const blogPosts = await getAllSlugs(sanityClient, 'blogPost')
  const allLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, allLocales)
  const paths: StaticPathWithSlug[] = []

  blogPosts.forEach((blogPost) => {
    const locale = blogPost.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogPost.slug.current,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogPost.slug.current,
          },
          locale: childLocale,
        })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogPostPage
